import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../services/authContext';
import { Spinner } from '../../components/common/Spinner';
import { apiPost } from '../../services/api/client';

export function CallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthToken, refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double execution from React StrictMode
    if (hasProcessed.current) return;

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(getErrorMessage(errorParam));
        return;
      }

      if (code) {
        hasProcessed.current = true;
        try {
          // Exchange auth code for token via apiPost so we get the shared
          // 30s timeout / AbortController (F18-004). apiPost already sends
          // `credentials: 'include'` so the backend's Set-Cookie
          // (auth_token, auth_logged_in) is still accepted by the browser
          // — both for same-origin proxy (cookie binds to :3001 in dev)
          // and cross-origin (CORS Allow-Credentials is on).
          const { data } = await apiPost<{ data: { token: string } }>('/auth/exchange', { code });
          setAuthToken(data.token);
          await refreshUser();
          navigate('/dashboard');
        } catch (err) {
          hasProcessed.current = false; // Allow retry on error
          console.error('Auth code exchange failed:', err);
          setError('Failed to complete authentication. Please try again.');
        }
      } else {
        setError('Authentication failed. No authorization code received.');
      }
    };

    handleCallback();
  }, [searchParams, navigate, setAuthToken, refreshUser]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm rounded-lg bg-white p-8 shadow-lg text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="mt-4 text-lg font-semibold text-gray-900">Authentication Failed</h2>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-6 w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <Spinner size="lg" />
        <p className="mt-4 text-gray-500">Completing authentication...</p>
      </div>
    </div>
  );
}

function getErrorMessage(code: string): string {
  switch (code) {
    case 'auth_failed':
      return 'Authentication failed. Please try signing in again with your work account.';
    case 'unauthorized_domain':
      return 'Your email domain is not authorized for this instance. Contact an administrator.';
    case 'account_disabled':
      return 'Your account has been disabled. Please contact an administrator.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}
